import { useState, useEffect, useCallback } from "react";
import {
  Search,
  Users,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar,
  Mail,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  FileSpreadsheet,
  Send,
  Bell,
  Trash2,
  X,
} from "lucide-react";
import {
  useDeleteTicket,
  useGetAllTickets,
  useSendBulkReminder,
  useSendSingleReminder,
} from "../lib/api/ticket";
import * as XLSX from "xlsx";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

// Toast types
interface Toast {
  id: number;
  title: string;
  description: string;
  type: "success" | "error";
}

export default function AdminTickets() {
  // API Hooks
  const { data, isLoading, isError, refetch, isFetching } = useGetAllTickets();
  const { mutate: sendBulkReminder, isPending: isSendingBulk } =
    useSendBulkReminder();
  const { mutate: sendSingleReminder, isPending: isSendingSingle } =
    useSendSingleReminder();
  const { mutateAsync: deleteTicket, isPending: isDeleting } =
    useDeleteTicket();

  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "attending" | "cancelled"
  >("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(
    null
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<{
    id: string;
    fullName: string;
  } | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Toast helpers
  const showToast = useCallback(
    (title: string, description: string, type: "success" | "error") => {
      const id = Date.now();
      setToasts((prev) => [...prev, { id, title, description, type }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4000);
    },
    []
  );

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Process tickets data
  const tickets = Array.isArray(data?.data)
    ? data?.data
    : Array.isArray(data)
      ? data
      : [];

  // Filter tickets based on search and status
  const filteredTickets = tickets?.filter((ticket) => {
    const matchesSearch =
      searchTerm === "" ||
      ticket.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalFilteredItems = filteredTickets?.length || 0;
  const totalPages = Math.ceil(totalFilteredItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTickets = filteredTickets?.slice(startIndex, endIndex);

  // Stats
  const totalGuests = tickets?.length;
  const attendingCount = tickets?.filter(
    (t) => t.status === "attending"
  ).length;
  const cancelledCount = tickets?.filter(
    (t) => t.status === "cancelled"
  ).length;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date for Excel export
  const formatDateForExcel = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Export to Excel
  const exportToExcel = (exportAll: boolean = true) => {
    setIsExporting(true);

    try {
      const dataToExport = exportAll ? tickets : filteredTickets;

      if (!dataToExport || dataToExport.length === 0) {
        showToast("Export Failed", "No data to export.", "error");
        setIsExporting(false);
        return;
      }

      const excelData = dataToExport.map((ticket, index) => ({
        "#": index + 1,
        "Full Name": ticket.fullName || "Unknown",
        Email: ticket.email || "N/A",
        Status:
          ticket.status?.charAt(0).toUpperCase() + ticket.status?.slice(1) ||
          "Unknown",
        "RSVP Date": ticket.createdAt
          ? formatDateForExcel(ticket.createdAt)
          : "N/A",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = [
        { wch: 5 },
        { wch: 25 },
        { wch: 30 },
        { wch: 12 },
        { wch: 20 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "Guest List");

      const today = new Date().toISOString().split("T")[0];
      const filterSuffix =
        !exportAll && statusFilter !== "all" ? `_${statusFilter}` : "";
      const filename = `Wedding_Guest_List${filterSuffix}_${today}.xlsx`;

      XLSX.writeFile(workbook, filename);
      showToast(
        "Export Complete",
        `Exported ${dataToExport.length} guests.`,
        "success"
      );
    } catch (error) {
      console.error("Export failed:", error);
      showToast("Export Failed", "Please try again.", "error");
    } finally {
      setIsExporting(false);
    }
  };

  // Send bulk reminder to all attending guests
  const handleSendBulkReminder = () => {
    sendBulkReminder(undefined, {
      onSuccess: (data) => {
        setShowReminderConfirm(false);
        const result = data.data;
        if (result.failed === 0) {
          showToast(
            "Reminders Sent!",
            `Successfully sent ${result.sent} reminder emails.`,
            "success"
          );
        } else {
          showToast(
            "Partially Sent",
            `Sent ${result.sent} of ${result.total}. ${result.failed} failed.`,
            "error"
          );
        }
      },
      onError: (error) => {
        showToast(
          "Failed to Send",
          error.response?.data?.message || "Something went wrong!",
          "error"
        );
      },
    });
  };

  // Send single reminder to a specific guest
  const handleSendSingleReminder = (ticketId: string) => {
    setSendingReminderId(ticketId);
    sendSingleReminder(ticketId, {
      onSuccess: (data) => {
        showToast(
          "Reminder Sent!",
          `Reminder email sent to ${data.data.fullName}.`,
          "success"
        );
      },
      onError: (error) => {
        showToast(
          "Failed to Send",
          error.response?.data?.message || "Something went wrong!",
          "error"
        );
      },
      onSettled: () => {
        setSendingReminderId(null);
      },
    });
  };

  // Delete handlers
  const handleDeleteClick = (id: string, fullName: string) => {
    setTicketToDelete({ id, fullName });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ticketToDelete) return;
    const name = ticketToDelete.fullName;
    await deleteTicket(ticketToDelete.id, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setTicketToDelete(null);
        showToast("Guest Deleted", `${name} has been removed.`, "success");
      },
      onError: (error) => {
        showToast(
          "Delete Failed",
          error.response?.data?.message || "Something went wrong!",
          "error"
        );
      },
    });
    refetch();
  };

  // Pagination handlers
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#C49355] mx-auto mb-4" />
          <p className="text-gray-600">Loading RSVPs...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Failed to load tickets</p>
          <button
            onClick={() => refetch()}
            className="px-6 py-2 bg-[#3d3d3d] text-white rounded-lg hover:bg-[#2d2d2d] transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3F0] py-8 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-3xl md:text-4xl font-medium mb-2 bg-gradient-to-r from-[#C49355] to-[#E8B46A] bg-clip-text text-transparent"
              style={{ fontFamily: "'Times New Roman', Times, serif" }}
            >
              Guest List
            </h1>
            <p className="text-gray-600">
              Manage all RSVPs for Joseph & Jennifer's Wedding
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Send Reminder Button */}
            <button
              onClick={() => setShowReminderConfirm(true)}
              disabled={isSendingBulk || attendingCount === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#C49355] text-white rounded-lg hover:bg-[#b38347] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSendingBulk ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Bell className="w-4 h-4" />
              )}
              Send Reminder
            </button>

            {/* Export Dropdown */}
            <div className="relative group">
              <button
                disabled={isExporting || tickets?.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                Export
                <Download className="w-3 h-3" />
              </button>

              {/* Export Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <button
                  onClick={() => exportToExcel(true)}
                  disabled={isExporting}
                  className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg transition flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export All ({totalGuests})
                </button>
                {(searchTerm || statusFilter !== "all") && (
                  <button
                    onClick={() => exportToExcel(false)}
                    disabled={isExporting}
                    className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition flex items-center gap-2 border-t border-gray-100"
                  >
                    <Download className="w-4 h-4" />
                    Export Filtered ({totalFilteredItems})
                  </button>
                )}
              </div>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Total Guests */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#C49355] to-[#E8B46A] flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Guests</p>
                <p className="text-2xl font-semibold text-gray-800">
                  {totalGuests}
                </p>
              </div>
            </div>
          </div>

          {/* Attending */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Attending</p>
                <p className="text-2xl font-semibold text-green-600">
                  {attendingCount}
                </p>
              </div>
            </div>
          </div>

          {/* Cancelled */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Cancelled</p>
                <p className="text-2xl font-semibold text-red-500">
                  {cancelledCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C49355]/50 focus:border-[#C49355] transition"
              />
            </div>

            {/* Status Filter Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter("all")}
                className={`px-4 py-2 rounded-lg text-sm transition ${
                  statusFilter === "all"
                    ? "bg-[#3d3d3d] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter("attending")}
                className={`px-4 py-2 rounded-lg text-sm transition ${
                  statusFilter === "attending"
                    ? "bg-green-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Attending
              </button>
              <button
                onClick={() => setStatusFilter("cancelled")}
                className={`px-4 py-2 rounded-lg text-sm transition ${
                  statusFilter === "cancelled"
                    ? "bg-red-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Cancelled
              </button>
            </div>
          </div>
        </div>

        {/* Results Count & Items Per Page */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <p className="text-sm text-gray-500">
            Showing {totalFilteredItems > 0 ? startIndex + 1 : 0}-
            {Math.min(endIndex, totalFilteredItems)} of {totalFilteredItems}{" "}
            guests
            {totalFilteredItems !== totalGuests &&
              ` (filtered from ${totalGuests} total)`}
          </p>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C49355]/50"
            >
              {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500">per page</span>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">
                  #
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">
                  Guest
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">
                  Email
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">
                  RSVP Date
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedTickets?.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No guests found
                  </td>
                </tr>
              ) : (
                paginatedTickets?.map((ticket, index) => (
                  <tr key={ticket._id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {startIndex + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#C49355] to-[#E8B46A] flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-medium">
                            {ticket.fullName?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <span className="font-medium text-gray-800">
                          {ticket.fullName || "Unknown"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {ticket.email || "N/A"}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                          ticket.status === "attending"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {ticket.status === "attending" ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5" />
                        )}
                        {ticket.status
                          ? ticket.status.charAt(0).toUpperCase() +
                            ticket.status.slice(1)
                          : "Unknown"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {ticket.createdAt ? formatDate(ticket.createdAt) : "N/A"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {ticket.status === "attending" && (
                          <button
                            onClick={() => handleSendSingleReminder(ticket._id)}
                            disabled={
                              isSendingSingle &&
                              sendingReminderId === ticket._id
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#C49355] hover:bg-[#C49355]/10 rounded-lg transition disabled:opacity-50"
                            title="Send reminder email"
                          >
                            {isSendingSingle &&
                            sendingReminderId === ticket._id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Send className="w-3.5 h-3.5" />
                            )}
                            Remind
                          </button>
                        )}
                        <button
                          onClick={() =>
                            handleDeleteClick(ticket._id, ticket.fullName)
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Delete guest"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {paginatedTickets?.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-gray-500">
              No guests found
            </div>
          ) : (
            paginatedTickets?.map((ticket, index) => (
              <div
                key={ticket._id}
                className="bg-white rounded-xl p-5 shadow-sm"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#C49355] to-[#E8B46A] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium text-lg">
                        {ticket.fullName?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        #{startIndex + index + 1} {ticket.fullName || "Unknown"}
                      </p>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          ticket.status === "attending"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {ticket.status === "attending" ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        {ticket.status
                          ? ticket.status.charAt(0).toUpperCase() +
                            ticket.status.slice(1)
                          : "Unknown"}
                      </span>
                    </div>
                  </div>

                  {/* Mobile Action Buttons */}
                  <div className="flex items-center gap-1">
                    {ticket.status === "attending" && (
                      <button
                        onClick={() => handleSendSingleReminder(ticket._id)}
                        disabled={
                          isSendingSingle && sendingReminderId === ticket._id
                        }
                        className="p-2 text-[#C49355] hover:bg-[#C49355]/10 rounded-lg transition disabled:opacity-50"
                        title="Send reminder email"
                      >
                        {isSendingSingle && sendingReminderId === ticket._id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() =>
                        handleDeleteClick(ticket._id, ticket.fullName)
                      }
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Delete guest"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{ticket.email || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>
                      {ticket.createdAt ? formatDate(ticket.createdAt) : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm">
            {/* Mobile Page Info */}
            <p className="text-sm text-gray-500 sm:hidden">
              Page {currentPage} of {totalPages}
            </p>

            {/* Pagination Buttons */}
            <div className="flex items-center gap-1">
              {/* First Page */}
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="First page"
              >
                <ChevronsLeft className="w-5 h-5 text-gray-600" />
              </button>

              {/* Previous Page */}
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Previous page"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>

              {/* Page Numbers - Desktop Only */}
              <div className="hidden sm:flex items-center gap-1 mx-2">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 text-gray-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => goToPage(page as number)}
                      className={`min-w-[40px] h-10 rounded-lg text-sm font-medium transition ${
                        currentPage === page
                          ? "bg-gradient-to-r from-[#C49355] to-[#E8B46A] text-white"
                          : "hover:bg-gray-100 text-gray-600"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              {/* Next Page */}
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Next page"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>

              {/* Last Page */}
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Last page"
              >
                <ChevronsRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Desktop Page Info */}
            <p className="hidden sm:block text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">Wedding Date: April 22, 2026</p>
        </div>
      </div>

      {/* Bulk Reminder Confirmation Modal */}
      {showReminderConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => !isSendingBulk && setShowReminderConfirm(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal Content */}
          <div
            className="relative bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#C49355] to-[#E8B46A] flex items-center justify-center">
                <Bell className="w-8 h-8 text-white" />
              </div>

              {/* Title */}
              <h3
                className="text-xl font-medium text-gray-800 mb-2"
                style={{ fontFamily: "'Times New Roman', Times, serif" }}
              >
                Send Reminder Emails?
              </h3>

              {/* Description */}
              <p className="text-gray-600 mb-6">
                This will send reminder emails to all{" "}
                <span className="font-semibold text-green-600">
                  {attendingCount} attending guests
                </span>
                . Are you sure you want to continue?
              </p>

              {/* Warning Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <p className="text-amber-800 text-sm">
                  <strong>Note:</strong> This may take a few moments depending
                  on the number of guests.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReminderConfirm(false)}
                  disabled={isSendingBulk}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendBulkReminder}
                  disabled={isSendingBulk}
                  className="flex-1 px-4 py-3 bg-[#C49355] text-white rounded-lg hover:bg-[#b38347] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSendingBulk ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send All
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => {
            if (!isDeleting) {
              setDeleteModalOpen(false);
              setTicketToDelete(null);
            }
          }}
        >
          <div
            className="bg-white rounded-2xl p-8 md:p-12 max-w-md w-full text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setDeleteModalOpen(false);
                setTicketToDelete(null);
              }}
              disabled={isDeleting}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X size={24} />
            </button>

            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-10 h-10 text-red-500" />
              </div>
            </div>

            {/* Title */}
            <h2
              className="text-2xl md:text-3xl text-gray-800 mb-4"
              style={{ fontWeight: 400 }}
            >
              Delete Guest
            </h2>

            {/* Message */}
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete
            </p>
            <p className="text-gray-800 font-medium text-lg mb-6">
              {ticketToDelete?.fullName}?
            </p>
            <p className="text-gray-500 text-sm mb-8">
              This action cannot be undone.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="w-full px-8 py-4 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Yes, Delete"
                )}
              </button>

              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setTicketToDelete(null);
                }}
                disabled={isDeleting}
                className="w-full px-8 py-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto animate-slideIn max-w-sm w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
          >
            <div className="flex items-start gap-3 p-4">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  t.type === "success" ? "bg-green-100" : "bg-red-100"
                }`}
              >
                {t.type === "success" ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{t.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
              </div>
              <button
                onClick={() => dismissToast(t.id)}
                className="text-gray-400 hover:text-gray-600 transition flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div
              className={`h-1 animate-shrink ${
                t.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            />
          </div>
        ))}
      </div>

      {/* Toast Animation Styles */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
        }
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        .animate-shrink {
          animation: shrink 4s linear forwards;
        }
      `}</style>
    </div>
  );
}
